/**
 * Auftrag erteilen.
 *
 * Pflichtfeld Auftragswert mit de-AT-Formatierung während der Eingabe.
 * Enter bestätigt. Erfolgsfall: Toast. Fehlerfall beim Abgleich: kein Toast,
 * sondern das dauerhafte Sync-Banner am Angebot.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import { betragFormatieren, betragLesen, gruppiere } from '../hilfen/format';
import type { Angebot, AngebotAntwort } from '../typen';


/**
 * Schneidet einen abschliessenden Punkt weg.
 *
 * Die Fehlermeldungen von Graph enden mal mit Punkt und mal ohne. Ohne das
 * hier stuende im Satz "nicht konfiguriert.." – eine Kleinigkeit, die aber
 * jedem auffaellt, dem sie gezeigt wird.
 */
function ohnePunkt(text: string): string {
  return text.endsWith('.') ? text.slice(0, -1) : text;
}
export function AuftragModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const [eingabe, setEingabe] = useState(
    angebot.auftrag ? gruppiere(angebot.auftrag) : '',
  );
  const [fehler, setFehler] = useState('');
  const [grund, setGrund] = useState('');

  /*
   * Liegt im Ordner „Gültiger Stand" ein Plan?
   *
   * Vier Antworten, und der Unterschied ist der Sinn der Sache. Nachgefragt
   * wird nur bei „leer" – wenn der Ordner da ist und nichts darin liegt.
   * Konnte Kora nicht nachsehen, oder hat der Vorgang noch die frühere
   * Ordnerstruktur, steht hier eine Auskunft und kein Vorwurf.
   */
  const stand = useQuery({
    queryKey: ['planungsstand', angebot.id],
    queryFn: () =>
      api.hole<{
        stand: 'belegt' | 'leer' | 'ohneOrdner' | 'unbekannt';
        dateien: number;
        grund: string;
        ordner: string;
      }>(`/angebote/${angebot.id}/planungsstand`),
    staleTime: 0,
  });

  const leer = stand.data?.stand === 'leer';
  const ordnerLink = `kora://oeffne?art=angebot&ordner=${encodeURIComponent(
    angebot.ordnerName ?? '',
  )}&unter=${encodeURIComponent(stand.data?.ordner ?? '')}`;

  const mutation = useMutation({
    mutationFn: (auftrag: number) =>
      api.sende<AngebotAntwort>(`/angebote/${angebot.id}/auftrag`, {
        auftrag,
        standBegruendung: leer ? grund.trim() : '',
      }),
    onSuccess: (antwort) => {
      onFertig();
      if (antwort.toast) toastZeigen(antwort.toast);
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  function bestaetigen() {
    const wert = betragLesen(eingabe);
    if (!wert || wert <= 0) {
      setFehler('Bitte den Auftragswert eintragen – Pflichtfeld.');
      return;
    }
    if (leer && !grund.trim()) {
      setFehler('Bitte kurz begründen, warum in der Planung nichts Aktuelles liegt.');
      return;
    }
    mutation.mutate(wert);
  }

  return (
    <Modal
      titel="Auftrag erteilen"
      untertitel={`${angebot.nr}_${angebot.prefix}`}
      breite={470}
      abstandOben={70}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer ton="gruen" onClick={bestaetigen} disabled={mutation.isPending}>
            {mutation.isPending ? 'Wird gespeichert …' : 'Bestätigen'}
          </KnopfPrimaer>
        </>
      }
    >
      <label className="modal-feld">
        <span>Auftragswert · netto, vor Steuern</span>
        <span className={`modal-betrag ${fehler ? 'modal-betrag--fehler' : ''}`}>
          <input
            autoFocus
            value={eingabe}
            placeholder="0"
            inputMode="numeric"
            onChange={(e) => {
              setEingabe(betragFormatieren(e.target.value));
              setFehler('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') bestaetigen();
            }}
          />
          <span>€</span>
        </span>
      </label>

      <div className="modal-hinweis">
        Der Wert fließt in die Gewinnprognose unter Finanzen ein.
      </div>

      {leer && (
        <div className="au-stand">
          <div className="au-stand-titel">
            Im Ordner „{stand.data?.ordner}" liegt nichts Aktuelles.
          </div>
          <p className="au-stand-text">
            Dort gehören die Unterlagen hinein, nach denen gefertigt wird – Grundriss,
            Türliste, Positionsliste. Überholte Fassungen liegen im Unterordner und
            zählen hier nicht mit. Leg die aktuellen jetzt ab, oder schreib kurz auf,
            warum es keine gibt. Die Begründung wird mit deinem Namen und dem Zeitpunkt
            im Verlauf vermerkt.
          </p>
          <div className="au-stand-aktionen">
            <a className="au-stand-knopf" href={ordnerLink}>
              Ordner öffnen
            </a>
            <button
              type="button"
              className="au-stand-knopf"
              onClick={() => void stand.refetch()}
              disabled={stand.isFetching}
            >
              {stand.isFetching ? 'Sieht nach …' : 'Nochmal nachsehen'}
            </button>
          </div>
          <label className="modal-feld au-stand-feld">
            <span>Begründung</span>
            <textarea
              className="modal-eingabe"
              rows={2}
              value={grund}
              placeholder="z. B. Standardmaß ohne Plan, nach Aufmaß gefertigt"
              onChange={(e) => {
                setGrund(e.target.value);
                setFehler('');
              }}
            />
          </label>
        </div>
      )}

      {stand.data?.stand === 'ohneOrdner' && (
        <div className="modal-hinweis">
          Dieser Vorgang hat noch die frühere Ordnerstruktur – den Ordner
          „{stand.data.ordner}" gibt es dort nicht. Kora prüft deshalb nichts; die
          Pläne liegen bei diesen Vorgängen unter „2. Planung".
        </div>
      )}
      {stand.data?.stand === 'unbekannt' && (
        <div className="modal-hinweis">
          {`Kora konnte nicht in die Planung sehen${
            stand.data.grund ? ` – ${ohnePunkt(stand.data.grund)}` : ' – die Ablage war nicht erreichbar'
          }. Der Auftrag lässt sich trotzdem erteilen.`}
        </div>
      )}

      <div className="modal-hinweis">
        Nach dem Bestätigen verschiebt Kora den Vorgangsordner nach
        „Aufträge". Klappt einer der beiden Schritte nicht, bleibt ein Hinweis am Angebot stehen,
        bis er erfolgreich war.
      </div>

      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
